/**
 * DYN-3 Tier 3 — Polar Stretch action evaluator.
 *
 * Extracted from actions.ts to keep that file under the 1000-LOC size
 * guideline (CLAUDE.md). Exports the single `applyPolarStretch` entry
 * point plus an `entityFullyInsideFrame` predicate that the polar
 * stretch path uses to decide whether the whole-entity rotation is
 * safe for a given crossing frame.
 *
 * AutoCAD parity:
 *   Polar Stretch on a Polar Parameter performs THREE transformations
 *   on vertices inside its crossingFrame:
 *     1. Rotate around polar.basePoint by (polar.currentAngle -
 *        polar.defaultAngle) [+ angleOffset] (degrees).
 *     2. Translate by (polar.currentDistance - polar.defaultDistance)
 *        * distanceMultiplier along the CURRENT polar direction.
 *   Vertices outside crossingFrame are untouched (STRETCH semantics).
 *
 * Degenerate cases (returned as no-op without error):
 *   - Rotation delta == 0 AND translation magnitude == 0 → unchanged.
 *   - Non-polar driving parameter → error, selection unchanged.
 */

import type {
  DynamicParameter,
  PolarParameter,
  PolarStretchAction,
} from "@dxf-ai-editor/drawing-model";

import type { EntityLike, PolylineTableOleRefusalSink, Vec2 } from "./geometry";
import { rotateEntity, stretchEntity } from "./geometry";
// The SAME lift the crossing-frame classification in
// `stretchPolylineTableOleChild` uses — a heavy POLYLINE's group 10/20 are OCS,
// and a frame is authored against the drawing (Codex round 15, P2).
import { heavyPolylineDrawnVertexXY } from "./geometryPolylineTableOle";
import type { ParameterRuntimeValue } from "./parameters";

const DEG_TO_RAD = Math.PI / 180;

export interface PolarStretchApplication {
  readonly entities: readonly EntityLike[];
  readonly errors: readonly string[];
}

function actionLabel(a: PolarStretchAction): string {
  return a.name && a.name.length > 0 ? a.name : a.id;
}

/**
 * Apply a Polar Stretch action to a selection. See module docstring.
 */
export function applyPolarStretch(
  action: PolarStretchAction,
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
  selection: readonly EntityLike[],
  errors: string[],
  /**
   * Where a declined heavy child (`Polyline` / `Table` / `OLE Frame`) is
   * reported. Supplied by `actions.ts`'s dispatcher, which owns the bilingual
   * wording — passed IN rather than built here so this module never has to
   * import back into `actions.ts` (that edge would be a module cycle,
   * `.dependency-cruiser.cjs` `no-circular` = error).
   */
  refusals?: PolylineTableOleRefusalSink,
): PolarStretchApplication {
  if (parameter.kind !== "polar") {
    errors.push(
      `Action "${actionLabel(action)}" kind=polar-stretch requires polar parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  const pol = parameter as PolarParameter;
  const defAngleRad = pol.defaultAngle * DEG_TO_RAD;
  const curAngleRad = runtime.angle ?? pol.currentAngle * DEG_TO_RAD;
  const offsetRad = action.angleOffset * DEG_TO_RAD;
  const deltaAngleRad = curAngleRad - defAngleRad + offsetRad;

  const defDist = pol.defaultDistance;
  const curDist = runtime.distance ?? pol.currentDistance;
  const deltaDist = (curDist - defDist) * action.distanceMultiplier;
  const translation: Vec2 = {
    x: deltaDist * Math.cos(curAngleRad),
    y: deltaDist * Math.sin(curAngleRad),
  };

  const noRotation = deltaAngleRad === 0;
  const noTranslation = translation.x === 0 && translation.y === 0;
  if (noRotation && noTranslation) {
    return { entities: selection, errors };
  }

  let next: readonly EntityLike[] = selection;
  if (!noRotation) {
    // For Tier 3 we rotate the WHOLE entity only when every vertex is
    // inside the crossingFrame. Partial-inclusion degrades gracefully
    // to translation-only (AutoCAD documents partial-inclusion as
    // whole-entity transform, but our geometry helpers operate per-
    // entity rather than per-vertex — full per-vertex rotation is a
    // Tier 4 refinement).
    next = next.map((e) =>
      entityFullyInsideFrame(e, action.crossingFrame)
        ? rotateEntity(e, pol.basePoint, deltaAngleRad, refusals)
        : e,
    );
  }
  if (!noTranslation) {
    next = next.map((e) =>
      stretchEntity(e, action.crossingFrame, translation, refusals),
    );
  }
  return { entities: next, errors };
}

/**
 * Conservative "entity fully inside frame" predicate. Returns true when
 * every recognised anchor point of the entity falls inside the crossing
 * frame (closed rectangle). Used by applyPolarStretch.
 */
export function entityFullyInsideFrame(
  entity: EntityLike,
  frame: { corner1: Vec2; corner2: Vec2 },
): boolean {
  const e = entity as unknown as Record<string, unknown>;
  const xMin = Math.min(frame.corner1.x, frame.corner2.x);
  const xMax = Math.max(frame.corner1.x, frame.corner2.x);
  const yMin = Math.min(frame.corner1.y, frame.corner2.y);
  const yMax = Math.max(frame.corner1.y, frame.corner2.y);
  const inside = (p: { x: number; y: number }) =>
    p.x >= xMin && p.x <= xMax && p.y >= yMin && p.y <= yMax;
  const pts: Array<{ x: number; y: number }> = [];
  const maybePt = (v: unknown) => {
    if (
      v &&
      typeof v === "object" &&
      typeof (v as { x: unknown }).x === "number" &&
      typeof (v as { y: unknown }).y === "number"
    ) {
      pts.push(v as { x: number; y: number });
    }
  };
  maybePt(e.start);
  maybePt(e.end);
  maybePt(e.center);
  if (entity.type === "Polyline" && Array.isArray(e.vertices)) {
    // A heavy POLYLINE's stored group 10/20 are OCS coordinates, which under a
    // non-default group-210 extrusion sit nowhere near the grip the frame was
    // drawn around (extrusion `(0,0,-1)` puts a vertex stored at `(5,0)` on
    // screen at `(-5,0)`). Classifying it there is the same defect Codex
    // round 15 filed against the crossing-frame STRETCH, so it takes the same
    // one-truth lift rather than the raw `location`.
    for (const p of heavyPolylineDrawnVertexXY(entity)) pts.push(p);
  } else if (Array.isArray(e.vertices)) {
    // An LWPOLYLINE / Revision Cloud vertex IS the point; any other kind that
    // carries a `location` one level down (DXF 10/20/30 of a sub-record) is
    // probed there too. This is what lets an entity whose vertices all sit
    // inside the frame take the WHOLE-entity rotation half of Polar Stretch
    // instead of silently falling to the translation-only branch
    // (`pts.length === 0` → `false`).
    for (const v of e.vertices as unknown[]) {
      maybePt(v);
      if (v && typeof v === "object") {
        maybePt((v as { location?: unknown }).location);
      }
    }
  }
  if (pts.length === 0) return false;
  return pts.every(inside);
}
